import type { ReactNode } from 'react'
import { View } from 'react-native'

import { Icon } from '@/src/elements/media/Icon'
import { BarStepper } from '@/src/elements/stepper/bar'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import { useWizardStore } from '@/src/features/settings/stores/useWizardStore'
import type { TSourceParam } from '@/src/general/constants/routes'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { useOnChange } from '@/src/general/hooks/useOnChange'
import { getDateWithoutTimezone } from '@/src/general/utils/getDateWithoutTimezone'

import { AboutYouStep } from './wizard/AboutYouStep'
import { EndStep } from './wizard/EndStep'
import { ThemeStep } from './wizard/ThemeStep'
import { TopicsStep } from './wizard/TopicStep'

export const WizardScreen = ({ source }: TSourceParam): ReactNode => {
  const { data: me } = useMeQuery()

  const { currentStepName, step, totalSteps, setTags, setUserInfos } = useWizardStore()

  useOnChange(me?.id, () => {
    if (!me) return

    setTags(me.wizard?.tags || [])
    setUserInfos({
      gender: me.gender || undefined,
      countryCode: me.countryCode || undefined,
      location:
        me.city && me.countryCode && me.latitude && me.longitude
          ? {
              city: me.city,
              latitude: me.latitude,
              longitude: me.longitude,
              countryCode: me.countryCode,
              zipcode: me.zipcode,
            }
          : undefined,
      birthdate: getDateWithoutTimezone(me.birthdate)?.toDate() ?? undefined,
    })
  })

  if (!me) return null

  return (
    <View className="flex h-screen w-screen py-6 native:pt-safe-insets-t" testID={TEST_ID.ME.WIZARD.SCREEN}>
      <Icon name={ICON.STARS_BIG} className="absolute right-0 top-0 h-[234px] w-[234px]" />

      <View className="mx-auto h-full w-full max-w-lg">
        {step < totalSteps && (
          <BarStepper
            currentStep={step}
            totalSteps={totalSteps - 1} // -1 because the last step is the loader
            className="mb-10 px-6"
          />
        )}
        {currentStepName === 'theme' && <ThemeStep source={source} />}
        {currentStepName === 'topics' && <TopicsStep />}
        {currentStepName === 'about-you' && <AboutYouStep />}
        {currentStepName === 'end' && <EndStep />}
      </View>
    </View>
  )
}
